/* eslint-disable react/no-did-update-set-state */
import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Table,
  Header,
  Divider,
  Popup,
  Icon,
  Tab,
  Form,
  Progress,
  Button as SemanticButton,
} from 'semantic-ui-react';
import styled from 'styled-components';
import cn from 'classnames';
import Chart from 'react-chartjs-2';
import 'chartjs-chart-box-and-violin-plot/build/Chart.BoxPlot';
import { colors, portfolioColors, graph } from 'utils/colors';
import {
  Accounts,
  RiskProfile,
  Logging,
  ReallocateAccount,
  Portfolio,
} from 'actions';
import {
  Segment,
  Button,
  RingChart,
  RiskQuestionnaire,
  AccountsDropdown2,
} from 'components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { parseDate } from 'utils/helpers';
import assignColors from 'utils/assignColors';

import {
  faBullseye,
  faCheck,
  faChartPie,
  faShield,
  faExclamationTriangle,
  faSync,
  faArrowUpRightFromSquare,
  // eslint-disable-next-line import/extensions
} from '@fortawesome/free-solid-svg-icons';
// eslint-disable-next-line import/extensions
import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import RiskPortfoliosList from './RiskPortfoliosList';
import { RiskProfilesListMap } from './RiskProfilesListMap';
import RiskProfileTable from './RiskProfileTable';

class RiskProfilePage extends Component {
  state = {
    selectedPortfolio: null,
    recommendedPortfolio: null,
    recommendedCapacity: null,
    recommendedTolerance: null,
    riskProfiles: [],
    monitoredAccountIds: [],
    monitoredAccounts: [],
    monitoredAccountsChange: false,
    currentTab: 0,
  };

  componentDidMount() {
    const { dispatch, monitoringResult } = this.props;
    window.scrollTo(0, 0);

    dispatch(Accounts.fetchAll());
    dispatch(RiskProfile.getRiskProfile());
    if (!monitoringResult) {
      dispatch(RiskProfile.getRiskProfileMonitoring());
    } else {
      this.setState({
        selectedPortfolio: monitoringResult.risk_profile.risk_category,
        monitoredAccountIds: monitoringResult.monitored_accounts || [],
        currentTab: monitoringResult.risk_profile ? 2 : 0,
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { riskProfile, monitoringResult, accountList, dispatch } = this.props;
    const { currentTab, monitoredAccountIds } = this.state;

    if (riskProfile && prevProps.riskProfile !== riskProfile) {
      const riskProfiles = (riskProfile || [])
        .map((portfolio) => ({
          ...portfolio,
          ...RiskProfilesListMap[portfolio.risk_category],
        }))
        .sort((a, b) => a.equityPercent - b.equityPercent);

      this.setState({ riskProfiles });
    }

    if (monitoringResult && monitoringResult.risk_profile && currentTab !== 1) {
      if (prevProps.monitoringResult !== monitoringResult) {
        this.setState({
          selectedPortfolio: monitoringResult.risk_profile.risk_category,
          monitoredAccountIds: monitoringResult.monitored_accounts || [],
          currentTab: monitoringResult.risk_profile ? 2 : 0,
        });
      }
    }
    if (
      JSON.stringify(monitoredAccountIds) !==
        JSON.stringify(prevState.monitoredAccountIds) ||
      JSON.stringify(accountList) !== JSON.stringify(prevProps.accountList)
    ) {
      const monitoredAccounts = accountList
        ? accountList.filter((account) =>
            monitoredAccountIds.includes(account.id)
          )
        : [];
      this.setState({ monitoredAccounts });
    }
    if (
      currentTab === 0 &&
      prevState.currentTab !== 0 &&
      (!monitoringResult || !monitoringResult.risk_question_answers)
    ) {
      dispatch(Logging.logEvent('RiskProfileQuestionnaire_Started_Fresh'));
    }
    if (prevState.currentTab !== currentTab && currentTab === 2) {
      dispatch(Logging.logEvent('RiskProfileAlignment_View'));
    }
  }

  getChartData = (accounts) => {
    let hasCADAccounts = false;
    const exchangeRate =
      Number(localStorage.getItem('pwpa-exchange-rate')) || 1;

    const filteredAccounts = accounts.filter((account) => {
      if (account.currency === 'CAD') {
        hasCADAccounts = true;
      }
      return Object.values(account.asset_allocation).some(
        (value) => value !== 0
      );
    });

    const getUnmatchedValue = (account) =>
      (account.holdings || [])
        .filter((holding) => !holding.security_detail)
        .reduce((sum, holding) => {
          let value = holding.market_value;
          if (account.currency === 'USD' && hasCADAccounts) {
            value *= exchangeRate;
          }
          return sum + value;
        }, 0);
    const totalHoldings = filteredAccounts.reduce((total, account) => {
      const unmatched = getUnmatchedValue(account, hasCADAccounts);

      const balance =
        account.currency === 'USD' && hasCADAccounts
          ? (account.balance - account.cash) * exchangeRate - unmatched
          : account.balance - account.cash - unmatched;

      return total + balance;
    }, 0);

    const categories = ['Equity', 'Fixed Income', 'Alternative', 'Misc'];
    const allHoldings = filteredAccounts.reduce((result, account) => {
      const { asset_allocation } = account;
      const unmatched = getUnmatchedValue(account);

      const accountTotalHoldings =
        account.currency === 'USD' && hasCADAccounts
          ? (account.balance - account.cash) * exchangeRate - unmatched
          : account.balance - account.cash - unmatched;

      const weight = accountTotalHoldings / Math.abs(totalHoldings || 1);

      categories.forEach((category) => {
        const label = category === 'Misc' ? 'Misc.' : category;
        result[label] += asset_allocation[category] * weight;
      });

      return result;
    }, Object.fromEntries(categories.map((category) => [category === 'Misc' ? 'Misc.' : category, 0])));

    let hasNegative = false;
    const formattedHoldings = {};
    let totalAssetAllocation = 0;

    Object.entries(allHoldings).forEach(([category, value]) => {
      if (value < 0) {
        hasNegative = true;
      }
      if (value !== 0) {
        formattedHoldings[category] = value;
        totalAssetAllocation += value;
      }
    });

    return {
      labels: Object.keys(formattedHoldings),
      values: Object.values(formattedHoldings),
      filtered: formattedHoldings,
      negative: hasNegative,
      datasets: [
        {
          data: Object.values(formattedHoldings),
          backgroundColor: assignColors(Object.keys(formattedHoldings)),
        },
      ],
      totalAssetAllocation,
    };
  };

  handleRecommendChange = (name, capacity, tolerance) => {
    const { monitoringResult } = this.props;

    const savedPortfolio =
      monitoringResult && monitoringResult.risk_profile
        ? monitoringResult.risk_profile.risk_category
        : '';

    this.setState({
      recommendedPortfolio: name,
      recommendedCapacity: capacity,
      recommendedTolerance: tolerance,
    });
    this.handleRadioChange(null, {
      name: savedPortfolio || name,
    });
  };

  handleRadioChange = (e, { name }) => {
    const { monitoringResult } = this.props;

    const savedPortfolio =
      monitoringResult && monitoringResult.risk_profile
        ? monitoringResult.risk_profile.risk_category
        : '';
    if (name === savedPortfolio) {
      this.setState({ warning: false });
    }

    this.setState({
      selectedPortfolio: name,
    });
  };

  handleTabChange = (e, { activeIndex }) => {
    const { monitoringResult } = this.props;
    const { selectedPortfolio } = this.state;
    const disallowSwitchTab =
      activeIndex === 2 &&
      (!monitoringResult ||
        !monitoringResult.risk_profile ||
        (monitoringResult.risk_profile &&
          monitoringResult.risk_profile.risk_category !== selectedPortfolio));

    if (disallowSwitchTab) {
      this.setState({
        warning: true,
      });
    }

    if (!disallowSwitchTab) {
      return this.setState({
        currentTab: activeIndex,
        warning: false,
      });
    }
    return null;
  };

  handleSaveClick = (selectedPortfolio) => {
    const { recommendedPortfolio } = this.state;
    const { dispatch, riskProfile, monitoringResult } = this.props;

    const selectedPortfolioId = riskProfile.find(
      (p) => p.risk_category === selectedPortfolio
    ).id;

    let recommendedPortID;
    switch (recommendedPortfolio) {
      case 'All-In':
        recommendedPortID = 1;
        break;
      case 'Adventurous':
        recommendedPortID = 2;
        break;
      case 'Classic':
        recommendedPortID = 3;
        break;
      case 'Cautious':
        recommendedPortID = 4;
        break;
      case 'Playing-It-Safe':
        recommendedPortID = 5;
        break;
      default:
        recommendedPortID = 0;
    }

    if (recommendedPortID) {
      if (selectedPortfolioId === recommendedPortID) {
        dispatch(Logging.logEvent('RiskProfileAssign_Save_Result'));
      } else {
        dispatch(Logging.logEvent('RiskProfileAssign_Save_Diff'));
      }
    } else {
      dispatch(Logging.logEvent('RiskProfileAssign_Save_UserChoice'));
    }

    const params = {
      risk_profile: selectedPortfolioId,
    };
    if (monitoringResult) {
      dispatch(RiskProfile.updateRiskProfileMonitoring(params));
    } else {
      dispatch(RiskProfile.createRiskProfileMonitoring(params));
    }

    this.setState({
      currentTab: 2,
      warning: false,
    });
  };

  handleEvaluateClick = () => {
    const { dispatch, monitoringResult } = this.props;
    const { monitoredAccountIds } = this.state;
    const params = {
      risk_profile: monitoringResult.risk_profile.id,
      monitored_accounts: monitoredAccountIds.map((a) => Number(a)),
    };
    dispatch(Logging.logEvent('RiskProfileAlignment_Evaluate'));

    dispatch(RiskProfile.updateRiskProfileMonitoring(params));
    this.setState({ monitoredAccountsChange: false });
  };

  processBoxPlotData = (rp, selectedPortfolio) => {
    const sortedRiskProfile = [...rp].sort(
      (a, b) => a.risk_statistics.std.median - b.risk_statistics.std.median
    );

    return {
      labels: sortedRiskProfile.map(
        (item) => RiskProfilesListMap[item.risk_category].riskName
      ),
      datasets: [
        {
          label: 'Risk Profile',
          backgroundColor: sortedRiskProfile.map((item) =>
            item.risk_category === selectedPortfolio
              ? portfolioColors.lightBlue
              : '#D9D9D9'
          ),
          borderColor: 'black',
          borderWidth: 1,
          outlierColor: '#999999',
          padding: 10,
          itemRadius: 0,
          data: sortedRiskProfile.map((item) => {
            const { quartile1, median, quartile3, maximum, minimum } =
              item.risk_statistics.std;
            return {
              min: parseFloat((quartile1 * 100).toFixed(2)),
              q1: parseFloat((quartile1 * 100).toFixed(2)),
              median: parseFloat((median * 100).toFixed(2)),
              q3: parseFloat((quartile3 * 100).toFixed(2)),
              max: parseFloat((quartile3 * 100).toFixed(2)),
              whiskerMin: parseFloat((minimum * 100).toFixed(2)),
              whiskerMax: parseFloat((maximum * 100).toFixed(2)),
            };
          }),
          boxplot: {
            medianWidth: 8,
            boxplotWidth: 4,
            whiskerWidth: 4,
          },
        },
      ],
    };
  };

  renderAllocationTable = (rps) => {
    const { boxPlotContent, selectedPortfolio } = this.state;

    if (!rps || !rps.length) return null;

    const assetAllocMap = {
      'TSX Capped Composite': 'Canadian Stocks',
      'U.S. Total Market (CAD hedged)': 'U.S. Stocks',
      'Developed Markets (CAD hedged)': 'International Stocks',
      'Emerging Markets (CAD hedged)': 'Emerging Market Stocks',
      'Canadian Broad Bond Market': 'Canadian Bonds',
      'U.S. Aggregate Bond (CAD hedged)': 'U.S. Bonds',
    };

    // Union of categories across all portfolios, in first-seen order
    // Fixed display order: equities first, then bonds
    const preferredOrder = Object.keys(assetAllocMap);

    // Any categories the API returns that aren't in the map
    const extras = [];
    rps.forEach((rp) =>
      (rp.representing_portfolio || []).forEach(([name]) => {
        if (!preferredOrder.includes(name) && !extras.includes(name)) {
          extras.push(name);
        }
      })
    );

    // Keep only categories that actually appear in at least one portfolio
    const present = new Set(
      rps.flatMap((rp) =>
        (rp.representing_portfolio || []).map(([name]) => name)
      )
    );

    const assetCategories = [
      ...preferredOrder.filter((name) => present.has(name)),
      ...extras,
    ];

    // Lookup: risk_category -> { assetName: weight }
    const weightsByProfile = Object.fromEntries(
      rps.map((rp) => [
        rp.risk_category,
        Object.fromEntries(rp.representing_portfolio || []),
      ])
    );

    return (
      <div style={{ marginTop: 0 }}>
        <div
          style={{
            maxWidth: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: '1rem 4rem',
          }}
        >
          <Divider />
          <Header as="h3" style={{ margin: 0, fontWeight: 'bold' }}>
            <StyledButton
              icon
              onClick={() => this.setState({ boxPlotContent: !boxPlotContent })}
              isOpen={boxPlotContent}
            >
              <div style={{ margin: 0, padding: 0 }}>
                {boxPlotContent ? '-' : '+'}
              </div>
            </StyledButton>
            Wealthscope Target Asset Allocation for Risk Profiles
          </Header>

          {boxPlotContent && (
            <Table responsive style={{ margin: '1rem auto 0 auto' }}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Asset Allocation</Table.HeaderCell>
                  {rps.map((item) => (
                    <Table.HeaderCell key={item.id} textAlign="center">
                      {item.riskName}
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {assetCategories.map((category) => (
                  <Table.Row key={category}>
                    <Table.Cell>
                      {assetAllocMap[category] || category}{' '}
                      <Popup
                        trigger={<Icon name="question circle outline" />}
                        position="right center"
                        content={category}
                      />
                    </Table.Cell>
                    {rps.map((item) => {
                      const isSelected =
                        item.risk_category === selectedPortfolio;
                      const pct =
                        weightsByProfile[item.risk_category][category];
                      return (
                        <Table.Cell
                          key={item.id}
                          textAlign="center"
                          style={{
                            backgroundColor: isSelected
                              ? `${colors.periwinkle}34`
                              : 'transparent',
                            fontWeight: isSelected ? 'bold' : 'normal',
                          }}
                        >
                          {typeof pct === 'number'
                            ? `${(pct * 100).toFixed(1)}%`
                            : 'N/A'}
                        </Table.Cell>
                      );
                    })}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </div>
      </div>
    );
  };

  renderAlignmentTable = (chartData) => {
    const { selectedPortfolio } = this.state;

    if (!chartData.labels) return null;

    const equityPercent =
      selectedPortfolio && RiskProfilesListMap[selectedPortfolio]
        ? RiskProfilesListMap[selectedPortfolio].equityPercent
        : 0;
    const fixedIncomePercent = 100 - equityPercent;

    const targetMap = {
      Equity: equityPercent / 100,
      'Fixed Income': fixedIncomePercent / 100,
    };

    const extraLabels = (chartData.labels || []).filter(
      (l) => l !== 'Equity' && l !== 'Fixed Income'
    );
    const allLabels = ['Equity', 'Fixed Income', ...extraLabels];

    const actualMap = {};
    (chartData.labels || []).forEach((label, i) => {
      actualMap[label] = chartData.values[i];
    });

    return (
      <div style={{ marginTop: '0' }}>
        <div
          style={{
            maxWidth: '100%',
            display: 'flex',
            alignItems: 'left',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: '1rem 0',
          }}
        >
          <Table responsive style={{ margin: '1rem auto 0 auto' }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Asset Class</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Target</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Actual</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">
                  Deviations
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {allLabels.map((label) => {
                const target =
                  targetMap[label] !== undefined ? targetMap[label] : null;
                const actual =
                  actualMap[label] !== undefined ? actualMap[label] : 0;

                const deviation = target !== null ? actual - target : actual;
                const deviationFormatted =
                  deviation !== null
                    ? `${deviation >= 0 ? '+' : ''}${parseFloat(
                        (deviation * 100).toFixed(2)
                      )}%`
                    : '—';
                return (
                  <Table.Row key={label}>
                    <Table.Cell>{label}</Table.Cell>
                    <Table.Cell textAlign="center">
                      {target !== null
                        ? `${parseFloat((target * 100).toFixed(2))}%`
                        : '0%'}{' '}
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      {`${parseFloat((actual * 100).toFixed(2))}%`}{' '}
                    </Table.Cell>
                    <Table.Cell
                      textAlign="center"
                      style={{
                        color:
                          deviation !== null && Math.abs(deviation * 100) >= 10
                            ? colors.red
                            : 'inherit',
                      }}
                    >
                      {deviationFormatted}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
          <div
            style={{
              color: colors.darkGrey,
              marginTop: '0.5rem',
            }}
          >
            <Icon
              name="info circle"
              style={{ marginTop: '2px', flexShrink: 0 }}
            />
            <span>
              You'll be alerted when any asset class in your portfolio is more
              than 10 percentage points above or below your target allocation.
            </span>
          </div>
        </div>
      </div>
    );
  };

  renderTabs = () => {
    const { accountList, monitoringResult, user_region, dispatch, history } =
      this.props;
    const {
      selectedPortfolio,
      riskProfiles,
      monitoredAccountIds,
      recommendedPortfolio,
      monitoredAccounts,
      monitoredAccountsChange,
      currentTab,
    } = this.state;
    // Safely extract risk_profile and risk_statistics if monitoringResult exists
    const risk_profile = monitoringResult && monitoringResult.risk_profile;
    const risk_statistics =
      monitoringResult && monitoringResult.risk_statistics;
    const boxplotData = riskProfiles.length
      ? this.processBoxPlotData(riskProfiles, selectedPortfolio)
      : null;
    const savedPortfolio = risk_profile ? risk_profile.risk_category : '';
    const accountValueMap = new Map(
      (accountList || []).map((account) => [account.id, account.holdings])
    );
    const hasNegativeValueAccount = monitoredAccountIds.find((id) => {
      const holdings = accountValueMap.get(Number(id));
      return holdings && holdings.find((holding) => holding.market_value < 0);
    });

    const chartData = this.getChartData(monitoredAccounts || []);
    const equityPercent =
      selectedPortfolio && RiskProfilesListMap[selectedPortfolio]
        ? RiskProfilesListMap[selectedPortfolio].equityPercent
        : 0;
    const fixedIncomePercent = 100 - equityPercent;

    const targetMap = {
      Equity: equityPercent / 100,
      'Fixed Income': fixedIncomePercent / 100,
    };

    const actualMap = {};
    (chartData.labels || []).forEach((label, i) => {
      actualMap[label] = chartData.values[i];
    });

    const deviationDetected = ['Equity', 'Fixed Income'].some((label) => {
      const target = targetMap[label] !== undefined ? targetMap[label] : null;
      const actual = actualMap[label] !== undefined ? actualMap[label] : null;
      if (target === null || actual === null) return false;
      return Math.abs((actual - target) * 100) >= 10;
    });

    const panes = [
      {
        menuItem: 'Profile Questionnaire',
        render: () => (
          <Tab.Pane>
            <div>
              <RiskQuestionnaire
                riskResultChange={this.handleRecommendChange}
                onComplete={() => this.setState({ currentTab: 1 })}
                isRiskProfilePage
              />
            </div>
          </Tab.Pane>
        ),
      },
      {
        menuItem: 'Profile Confirmation',
        render: () => (
          // eslint-disable-next-line react/jsx-fragments
          <React.Fragment>
            <Tab.Pane>
              <Header as="h2" style={{ color: 'black', marginTop: '0' }}>
                <FontAwesomeIcon
                  icon={faCheck}
                  size="2xs"
                  style={{ marginRight: '1rem', fontSize: '21px' }}
                />
                Confirm Your Risk Profile
              </Header>
              <p
                style={{
                  color: 'black' /* '#6D6E71' */,
                  textAlign: 'start',
                }}
              >
                If you completed the questionnaire in the previous step, your
                matched profile is pre-selected below. You can select a
                different one if you wish before proceeding to the next step.
              </p>
              {recommendedPortfolio && (
                <div
                  style={{
                    padding: '1rem',
                    backgroundColor: '#F2F2F2',
                    borderRadius: '10px',
                    fontSize: '1.14rem',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <FontAwesomeIcon
                      color={colors.darkBlue}
                      icon={faBullseye}
                      style={{ marginRight: '1rem', fontSize: '21px' }}
                    />
                    Questionnaire Result:{' '}
                    <span
                      style={{ color: colors.darkBlue, fontWeight: 'bold' }}
                    >
                      {RiskProfilesListMap[recommendedPortfolio].riskName}
                    </span>{' '}
                    ({RiskProfilesListMap[recommendedPortfolio].riskGeneric})
                  </div>
                  {/* {recommendedCapacity && recommendedTolerance && (
                        <span>
                          <span style={{ marginRight: '2rem' }}>
                            Capacity: {recommendedCapacity}
                          </span>
                          <span>Tolerance: {recommendedTolerance}</span>
                        </span>
                      )} */}
                </div>
              )}
              <div style={{ margin: '2rem 0' }}>
                <RiskPortfoliosList
                  portfolios={riskProfiles}
                  portfolioID={
                    selectedPortfolio ? selectedPortfolio.toString() : null
                  }
                  annuityRate={false}
                  handlePortfolioChange={this.handleRadioChange}
                  selectedPortfolio={selectedPortfolio}
                  recommendedPortfolio={recommendedPortfolio}
                  style={{ margin: 'initial' }}
                />
              </div>
              {selectedPortfolio ? (
                <div>
                  <div
                    style={{
                      textAlign: 'center',
                      margin: '0 auto',
                    }}
                  >
                    <StyledProgress
                      percent={
                        RiskProfilesListMap[selectedPortfolio].equityPercent
                      }
                      color={colors.blue}
                      progress
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <p>
                      <Icon name="info circle" style={{ marginTop: '2px' }} />
                      {RiskProfilesListMap[selectedPortfolio].description}
                    </p>
                  </div>
                </div>
              ) : null}
            </Tab.Pane>

            <div>
              <div
                style={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                }}
              >
                <BorderedSection>
                  <div
                    style={{ width: '80%', maxWidth: '900px', margin: 'auto' }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        maxWidth: '300px',
                      }}
                    />
                    <Header
                      as="h3"
                      style={{
                        color: colors.darkBlue,
                        marginTop: 0,
                        textAlign: 'center',
                      }}
                    >
                      <span>
                        Annualized Volatility&nbsp;
                        <Popup
                          trigger={
                            <Icon
                              name="question circle outline"
                              style={{ fontSize: '1rem' }}
                            />
                          }
                          position="right center"
                          content={
                            user_region === 'US'
                              ? 'Calculated using monthly rolling four-year periods from April 2007 to present. A four-year horizon is chosen to enable portfolio risk monitoring for the medium term.'
                              : 'Calculated using monthly rolling four-year periods in CAD from April 2007 to present. A four-year horizon is chosen to enable portfolio risk monitoring for the medium term.'
                          }
                        />
                      </span>
                      <Header.Subheader>
                        Min, Max, Median, and Interquartile Range
                      </Header.Subheader>
                    </Header>
                    {boxplotData && (
                      <div>
                        <Chart
                          type="boxplot"
                          width={250}
                          height={120}
                          legend={{
                            display: false,
                          }}
                          data={boxplotData}
                          options={chartOptions}
                        />
                        {selectedPortfolio &&
                          RiskProfilesListMap[selectedPortfolio] && (
                            <div style={{ textAlign: 'center' }}>
                              <Icon name="info circle" />
                              {(() => {
                                const selected = riskProfiles.find(
                                  (rp) => rp.risk_category === selectedPortfolio
                                );
                                const q1 = selected
                                  ? parseFloat(
                                      (
                                        selected.risk_statistics.std.quartile1 *
                                        100
                                      ).toFixed(2)
                                    )
                                  : null;
                                const q3 = selected
                                  ? parseFloat(
                                      (
                                        selected.risk_statistics.std.quartile3 *
                                        100
                                      ).toFixed(2)
                                    )
                                  : null;
                                return (
                                  // eslint-disable-next-line react/jsx-fragments
                                  <React.Fragment>
                                    For the{' '}
                                    {
                                      RiskProfilesListMap[selectedPortfolio]
                                        .riskName
                                    }{' '}
                                    portfolio, the middle 50% of historical
                                    annualized volatility falls between{' '}
                                    <b>
                                      {q1 !== null ? `${q1.toFixed(2)}%` : '—'}
                                    </b>{' '}
                                    and{' '}
                                    <b>
                                      {q3 !== null ? `${q3.toFixed(2)}%` : '—'}
                                    </b>
                                    .
                                  </React.Fragment>
                                );
                              })()}
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                  {this.renderAllocationTable(riskProfiles)}
                </BorderedSection>
              </div>

              <div
                style={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexDirection: 'row',
                  margin: '0',
                }}
              >
                <Button
                  className="left"
                  color={colors.blue}
                  icon="chevron left"
                  onClick={() => this.setState({ currentTab: 0 })}
                >
                  Back
                </Button>

                <Popup
                  trigger={
                    <div>
                      <Button
                        className={cn('left', {
                          disabled:
                            !selectedPortfolio ||
                            (selectedPortfolio &&
                              savedPortfolio === selectedPortfolio),
                        })}
                        color={colors.blue}
                        icon="save"
                        onClick={() => this.handleSaveClick(selectedPortfolio)}
                        disabled={
                          !selectedPortfolio ||
                          (selectedPortfolio &&
                            selectedPortfolio === savedPortfolio)
                        }
                      >
                        Save
                      </Button>
                    </div>
                  }
                  position="right center"
                  style={{
                    opacity:
                      !selectedPortfolio ||
                      (selectedPortfolio &&
                        selectedPortfolio === savedPortfolio)
                        ? 1
                        : 0,
                  }}
                  content="This profile has been previously saved."
                />
              </div>
            </div>
          </React.Fragment>
        ),
      },
      {
        menuItem: {
          key: 'riskMonitoring',
          content: 'Risk Alignment',
        },
        render: () => (
          // eslint-disable-next-line react/jsx-fragments
          <React.Fragment>
            <Tab.Pane style={{ padding: 0 }}>
              <div style={{ padding: '2rem' }}>
                <Header size="large">
                  <FontAwesomeIcon
                    icon={faBullseye}
                    style={{ marginRight: '1rem', fontSize: '21px' }}
                  />
                  Risk Profile Monitoring System
                </Header>
                <p>
                  Monitor your portfolio alignment against your risk profile.
                </p>
                <div
                  style={{
                    display: 'flex',
                    width: '100%',
                    marginTop: '1.5rem',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginBottom: '1.5rem',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'top',
                      width: '60%',
                    }}
                  >
                    <div>
                      <Form>
                        <Form.Field>
                          <label style={{ fontSize: '14px' }}>
                            SELECT ACCOUNTS TO MONITOR
                          </label>
                          <AccountsDropdown2
                            inputs={{}}
                            accountList={accountList}
                            selectedAccList={monitoredAccountIds || []}
                            excludedList={[]} // Adjust as necessary
                            savedAccountsValue={0} // Adjust as necessary
                            assignAccountsValue={(selectedAccounts) => {
                              // Update monitoredAccountIds in the state
                              this.setState({
                                monitoredAccountIds: selectedAccounts,
                                monitoredAccountsChange: true,
                              });
                            }}
                            float={false}
                            width={200}
                            hideAccountValue
                            showSelectAll
                            zIndex={0}
                          />
                        </Form.Field>
                      </Form>
                      <Button
                        className={cn('right', {
                          disabled:
                            !monitoredAccountIds ||
                            !monitoredAccountIds.length ||
                            hasNegativeValueAccount,
                        })}
                        icon="file"
                        color={colors.blue}
                        onClick={this.handleEvaluateClick}
                        disabled={
                          !monitoredAccountIds ||
                          !monitoredAccountIds.length ||
                          hasNegativeValueAccount
                        }
                      >
                        Evaluate
                      </Button>
                      {hasNegativeValueAccount ? (
                        <div
                          style={{
                            marginTop: '10px',
                            color: 'red',
                          }}
                        >
                          One or more of your selected accounts cannot be
                          analyzed
                          <br />
                          due to negative weights. Please select another.
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '14px', margin: 0 }}>RISK PROFILE</p>
                    <Header style={{ margin: '1rem 0' }}>
                      {RiskProfilesListMap[selectedPortfolio].riskName}
                    </Header>
                    <p style={{ fontSize: '14px' }}>
                      {RiskProfilesListMap[selectedPortfolio].equityPercent}%
                      Equity /{' '}
                      {100 -
                        RiskProfilesListMap[selectedPortfolio].equityPercent}
                      % Bonds
                    </p>
                  </div>
                </div>

                {/* Risk Profile Representation */}
                {selectedPortfolio && (
                  <div
                    style={{
                      margin: '0 2rem 0 0',
                      textAlign: 'center',
                      width: '100%',
                    }}
                  >
                    <StyledProgress
                      percent={
                        RiskProfilesListMap[selectedPortfolio].equityPercent
                      }
                      color={colors.blue}
                      progress
                    />
                    {monitoringResult &&
                      monitoringResult.risk_question_updated_on && (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div>
                            <FontAwesomeIcon
                              icon={faCalendar}
                              style={{ marginRight: '0.5rem' }}
                            />
                            {parseDate(
                              monitoringResult.risk_question_updated_on
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                )}
              </div>
            </Tab.Pane>
            {!monitoredAccountsChange && (
              <BorderedSection>
                <Header
                  size="large"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>
                    <FontAwesomeIcon
                      icon={faChartPie}
                      style={{
                        color: 'transparent',
                        stroke: 'black',
                        strokeWidth: '30px',
                        overflow: 'visible',
                        marginRight: '1rem',
                        fontSize: '21px',
                      }}
                    />
                    Asset Allocation Alignment
                  </span>
                  {deviationDetected && (
                    <DeviationBadge>
                      <Icon name="warning sign" />
                      Deviation Detected
                    </DeviationBadge>
                  )}
                </Header>{' '}
                <p>
                  The first dimension of risk profile alignment is asset
                  allocation: how does the asset mix in your actual portfolio
                  differ from your target?
                </p>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-around',
                  }}
                >
                  <div>
                    <div
                      style={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        marginBottom: '0.5rem',
                      }}
                    >
                      TARGET
                    </div>
                    <RingChart
                      labels={['Equity', 'Fixed Income']}
                      values={[
                        RiskProfilesListMap[selectedPortfolio].equityPercent /
                          100,
                        (100 -
                          RiskProfilesListMap[selectedPortfolio]
                            .equityPercent) /
                          100,
                      ]}
                      colors={assignColors(['Equity', 'Fixed Income'])}
                      maxHeight={350}
                      maxWidth={350}
                    />
                  </div>
                  <div>
                    <div
                      style={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        marginBottom: '0.5rem',
                      }}
                    >
                      ACTUAL
                    </div>
                    <RingChart
                      labels={chartData.labels}
                      values={chartData.values}
                      colors={assignColors(chartData.labels)}
                      maxHeight={350}
                      maxWidth={350}
                    />
                  </div>
                </div>
                <div style={{ width: '40rem', margin: '1rem auto' }}>
                  <ChartLegend>
                    <div>
                      <ColorSquare color={graph.red} /> Equity
                    </div>
                    <div>
                      <ColorSquare color={graph.blue} /> Fixed Income
                    </div>
                    <div>
                      <ColorSquare color={graph.pink} /> Alternative
                    </div>
                    <div>
                      <ColorSquare color={graph.teal} /> Misc.
                    </div>
                  </ChartLegend>
                </div>
                {this.renderAlignmentTable(chartData)}
              </BorderedSection>
            )}

            {risk_statistics && !monitoredAccountsChange && (
              <BorderedSection>
                <Header
                  size="large"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>
                    <FontAwesomeIcon
                      icon={faShield}
                      style={{ marginRight: '1rem', fontSize: '21px' }}
                    />
                    Portfolio Risk Alignment
                  </span>
                  {risk_statistics &&
                    risk_statistics.current_values &&
                    risk_statistics.current_values.std &&
                    risk_statistics.current_values.std.outof_bounds && (
                      <DeviationBadge>
                        <Icon name="warning sign" />
                        Deviation Detected
                      </DeviationBadge>
                    )}
                </Header>
                <p>
                  Another dimension of risk profile alignment is portfolio risk.
                  Below, the risk of your actual profile is compared to a
                  benchmark, using a globally diversified, passive ETF portfolio
                  that matches your target asset allocation.
                </p>
                {risk_statistics ? (
                  <RiskProfileTable
                    result={{ risk_profile, risk_statistics }}
                  />
                ) : null}
              </BorderedSection>
            )}

            {!monitoredAccountsChange &&
              ((risk_statistics &&
                risk_statistics.current_values &&
                risk_statistics.current_values.std &&
                risk_statistics.current_values.std.outof_bounds) ||
                deviationDetected) && (
                <BorderedSection>
                  <DeviationAlert>
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      style={{
                        flexShrink: 0,
                        marginTop: '5px',
                        fontSize: '16px',
                      }}
                    />
                    <p>
                      {risk_statistics &&
                      risk_statistics.current_values.std.outof_bounds &&
                      deviationDetected ? (
                        // eslint-disable-next-line react/jsx-fragments
                        <React.Fragment>
                          Portfolio deviation detected — your asset allocation
                          is currently more than 10 percentage points off your
                          target, and your portfolio risk falls outside the
                          target range for your risk profile.
                        </React.Fragment>
                      ) : deviationDetected ? (
                        // eslint-disable-next-line react/jsx-fragments
                        <React.Fragment>
                          Portfolio deviation detected — your asset allocation
                          is more than 10 percentage points off your target, but
                          your portfolio risk is still within the expected
                          range.
                        </React.Fragment>
                      ) : (
                        // eslint-disable-next-line react/jsx-fragments
                        <React.Fragment>
                          Portfolio deviation detected — your asset allocation
                          is on track, but your portfolio risk falls outside the
                          target range for your risk profile. This means your
                          portfolio may be experiencing more or less volatility
                          than expected.
                        </React.Fragment>
                      )}
                    </p>
                  </DeviationAlert>
                  {deviationDetected && (
                    <div>
                      <h3
                        style={{
                          margin: '0 0 0.5rem 0',
                          fontWeight: 'bold',
                          fontSize: '21px',
                        }}
                      >
                        Considering Rebalancing?
                      </h3>
                      <p
                        style={{
                          margin: '0 0 1.25rem 0',
                          color: '#555',
                          fontSize: '1.14rem',
                        }}
                      >
                        If you have a target portfolio saved, you can calculate
                        the trades required to reallocate your current holdings
                        to your target.
                      </p>

                      <ReallocateCard
                        onClick={() => {
                          dispatch(ReallocateAccount.resetReallocate());
                          dispatch(
                            ReallocateAccount.updateParameter(
                              'selectedAccount',
                              [...monitoredAccounts]
                            )
                          );
                          dispatch(
                            ReallocateAccount.updateParameter('currentPage', 1)
                          );
                          window.scrollTo(0, 0);
                          dispatch(Portfolio.resetPorts());
                          history.push(`/tools/portfolio-reallocate`);
                        }}
                      >
                        <ReallocateIconWrapper>
                          <FontAwesomeIcon
                            icon={faSync}
                            style={{ fontSize: '18px', color: 'white' }}
                          />
                        </ReallocateIconWrapper>
                        <div>
                          <div style={{ fontWeight: 'bold', fontSize: '19px' }}>
                            Open Portfolio Reallocator
                          </div>
                          <div
                            style={{
                              fontSize: '14px',
                              color: '#666',
                              marginTop: '2px',
                            }}
                          >
                            Your monitored account(s) will be pre-selected.
                          </div>
                        </div>
                      </ReallocateCard>
                      <Divider style={{ margin: '1.5rem 0 0.5rem' }} />

                      <p
                        style={{
                          fontSize: '1.14rem',
                          color: '#666',
                          margin: '1.25rem 0 0.6rem 0',
                        }}
                      >
                        Don't have a target portfolio yet? Create one using:
                      </p>
                      <div style={{ display: 'flex', gap: '0.75rem' }}>
                        {/* <SecondaryButton
                          onClick={() => console.log(monitoredAccounts)}
                        >
                          What-If Portfolio Builder
                        </SecondaryButton> */}
                        <SecondaryButton
                          onClick={() => {
                            window.scrollTo(0, 0);
                            history.push(`/portfolio/create`);
                          }}
                        >
                          Portfolio Builders
                          <FontAwesomeIcon
                            icon={faArrowUpRightFromSquare}
                            style={{
                              flexShrink: 0,
                              marginLeft: '10px',
                              fontSize: '16px',
                              color: colors.blue,
                            }}
                          />
                        </SecondaryButton>
                      </div>
                    </div>
                  )}
                </BorderedSection>
              )}
          </React.Fragment>
        ),
      },
    ];

    return (
      <Tab
        panes={panes}
        activeIndex={currentTab}
        onTabChange={this.handleTabChange}
      />
    );
  };

  render() {
    const { warning } = this.state;
    return (
      <Segment>
        {warning ? (
          <p
            style={{
              color: `${colors.red}`,
              fontWeight: 'bold',
              position: 'absolute',
              right: '2rem',
              top: '1rem',
            }}
          >
            <Icon name="warning" style={{}} />
            Save your selection to continue to the next tab.
          </p>
        ) : null}
        {this.renderTabs()}
      </Segment>
    );
  }
}

RiskProfilePage.propTypes = {
  user_region: PropTypes.string,
  riskProfile: PropTypes.array,
  dispatch: PropTypes.func.isRequired,
  accountList: PropTypes.array,
  monitoringResult: PropTypes.object,
  history: PropTypes.object,
};

RiskProfilePage.defaultProps = {
  user_region: 'CA',
  riskProfile: [],
  accountList: [],
  monitoringResult: null,
  history: {},
};

export default withRouter(
  connect((state) => ({
    user_region: localStorage.getItem('user_region'),
    riskProfile: state.RiskProfile.riskProfile,
    accountList: state.Accounts.accountList,
    monitoringResult: state.RiskProfile.monitoringResult,
  }))(RiskProfilePage)
);

const chartOptions = {
  hover: {
    animationDuration: 0,
    onHover: (e) => {
      e.target.style.cursor = 'pointer';
    },
  },
  responsive: true,
  layout: {
    padding: {
      top: 40,
      bottom: 40,
      left: 40,
      right: 40,
    },
  },
  animation: {
    duration: 1000,
    easing: 'easeOutQuart',
  },
  scales: {
    xAxes: [
      {
        gridLines: {
          display: false,
        },
        ticks: {
          fontColor: '#000',
          fontSize: 12,
          fontWeight: 'bold',
          maxRotation: 0,
          minRotation: 0,
        },
        categoryPercentage: 0.6,
        barPercentage: 0.6,
      },
    ],
    yAxes: [
      {
        gridLines: {
          color: 'rgba(200, 200, 200, 0.3)',
          borderDash: [3, 3],
        },
        ticks: {
          beginAtZero: true,
          max: 22,
          stepSize: 2,
          callback(value) {
            return `${value}%`;
          },
          fontColor: '#000',
          fontSize: 14,
        },
        scaleLabel: {
          display: true,
          labelString: 'Standard Deviation of Monthly Returns',
          fontColor: '#000',
          fontSize: 14,

          padding: {
            top: 0,
            bottom: 20,
          },
        },
      },
    ],
  },
  tooltips: {
    enabled: true,
    callbacks: {
      label(tooltipItem, data) {
        const dataset = data.datasets[tooltipItem.datasetIndex];
        const value = dataset.data[tooltipItem.index];

        return [
          `Maximum: ${value.whiskerMax}%`,
          `Q3: ${value.q3}%`,
          `Median: ${value.median}%`,
          `Q1: ${value.q1}%`,
          `Minimum: ${value.whiskerMin}%`,
        ];
      },
      title(tooltipItem, data) {
        return data.labels[tooltipItem[0].index];
      },
    },
    displayColors: false,
  },
  legend: {
    display: true,
    position: 'top',
    labels: {
      fontColor: '#333',
      fontSize: 14,
      boxWidth: 20,
      displayColors: false,
    },
  },
};

const StyledProgress = styled(({ className, children, color, ...rest }) => (
  <Progress className={className} {...rest}>
    {children}
  </Progress>
))`
  color: white !important;
  opacity: 100% !important;
  margin-bottom: 10px !important;
  background-color: ${colors.periwinkle} !important;
  height: 30px;
  .bar {
    background-color: ${colors.darkBlue} !important;
    height: 30px !important;
    border-top-right-radius: ${(props) =>
      props.percent >= 100 ? '.28571429rem' : '0'} !important;
    border-bottom-right-radius: ${(props) =>
      props.percent >= 100 ? '.28571429rem' : '0'} !important;
    .progress {
      display: none !important;
    }
    &::after {
      content: '${(props) => props.percent}% Equity';
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-weight: bold;
      font-size: 16px;
      white-space: nowrap;
      pointer-events: none;
    }
  }
  &::after {
    content: '${(props) =>
      props.percent === 100 ? '' : `${100 - props.percent}% Fixed Income`}';
    position: absolute;
    left: calc(
      ${(props) => props.percent}% + (100% - ${(props) => props.percent}%) / 2
    );
    top: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-weight: bold;
    font-size: 16px;
    white-space: nowrap;
    pointer-events: none;
  }
`;
const StyledButton = styled(SemanticButton)`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${(props) =>
    props.isOpen ? colors.blue : 'white'} !important;
  color: ${(props) => (props.isOpen ? 'white' : colors.blue)} !important;
  border: ${(props) =>
    props.isOpen ? 'none' : `2px solid ${colors.blue}`} !important;
  border-radius: 50% !important;
  height: 2rem;
  width: 2rem;
  transition: all 0.9s ease-in-out !important;
  font-size: 1.3rem !important;
  .icon {
    margin: 0;
    padding: 0;
    transform: translate(-10px, -10px);
  }
`;

const BorderedSection = styled.div`
  border: 1px solid #d9d9da;
  border-radius: 0.28571429rem;
  padding: 2rem;
  margin-bottom: 2rem;
  width: 100%;
`;

const ChartLegend = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  font-size: 16px;
`;

const ColorSquare = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 6px;
  margin-bottom: 3px;
  vertical-align: middle;
  background-color: ${(props) => props.color};
`;

const DeviationBadge = styled.div`
  font-size: 0.85rem;
  font-weight: normal;
  color: black;
  border: 1px solid ${colors.red};
  border-radius: 0.28571429rem;
  padding: 0.4rem 0.75rem;
  background-color: rgba(255, 184, 182, 0.25);
`;

// const NextStep = styled.div`
//   border: 1px solid ${colors.lightGrey};
//   border-radius: 0.28571429rem;
//   background-color: white;
//   padding: 1rem 2rem;
//   font-size: 1.14rem;
//   display: flex;
//   flex-direction: row;
//   justify-content: space-between;
//   margin: 1rem 0 0 0;
// `;

const DeviationAlert = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 1rem;
  font-weight: normal;
  color: black;
  border-radius: 0.28571429rem;
  padding: 2rem;
  background-color: rgba(255, 184, 182, 0.25);
  line-height: 1.5;
  margin-bottom: 1rem;
  border: 1px solid ${colors.red};
`;

const ReallocateCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  border: 1px solid #d9d9da;
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  background-color: white;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background-color: #f5f5f5;
  }
`;

const SecondaryButton = styled.button`
  background-color: white;
  border: 1px solid ${colors.blue};
  border-radius: 0.28571429rem;
  padding: 0.5rem 1rem;
  font-size: 1.14rem;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: #f0f2fc;
  }
`;

const ReallocateIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background-color: ${colors.blue};
  flex-shrink: 0;
`;
